/*!
 * EXPLOIT — FREE CHAPTERS LANDING (the sponsored-ad destination).
 *
 * A dedicated, conversion-first capture page for paid social / search ads that
 * promise the free opening of the novel. It resolves the tension named in the
 * brief: a FAST, message-matched email-capture hero above the fold for the
 * impatient ad-clicker, then an emotional, cinematic story unfolding below for
 * the curious, with ONE secondary push to the full /exploit microsite.
 *
 * WHY A SEPARATE PAGE (not /exploit): the research (task-tracker/research/
 * organic-reach-exploit-launch.md §5) is explicit — the paid landing must be a
 * stripped, single-goal capture with the ad's promise mirrored word-for-word,
 * minimal nav, one primary CTA, and a mobile LCP < 2.5s. /exploit is the rich,
 * considered book page for organic/entity traffic; this is the funnel mouth.
 *
 * THE CAPTURE IS THE PRODUCT. Both captures use S.capture({source:"free-chapter"})
 * — the ONLY source that lands a reader in the right list (egt.capture.js SOURCES;
 * server-allowlisted in app/routes/egt.email.capture.js) and triggers the sample
 * delivery. Do NOT invent a new capture; this flow is wired and verified.
 *
 * NON-NEGOTIABLES honoured (agents.md §4): frosted glass over UNDIMMED art — no
 * scrim/overlay/opacity-dim ever; true-black base; scarce gold; Playfair + Inter;
 * rem only. Motion is additive over a fully-present base (reduced-motion + no-JS
 * safe): app/public/src/egt.exploit.free.js only arms [data-free-motion].
 *
 * FOUNDER INSTRUCTIONS for THIS page: (1) the primary CTA is the "Send me the
 * opening" capture button; (2) the animated scroll cue sits clearly BELOW the
 * CTA/capture, never overlapping it (it is the last child of the hero column, in
 * normal flow — the same lesson learned on /exploit); (3) a clear SECONDARY CTA
 * pushes to the full microsite /exploit.
 *
 * 2026-08-21 FOUNDER DIRECTIVE — nothing on this site sells the book any more.
 * The preorder is gone from every public surface: the ghost "Or preorder the
 * first edition →" link under the closing capture, the preorder-cost FAQ entry,
 * the "place among The Named" clause, and the PreOrder Offer in the JSON-LD are
 * all removed. Two goals remain and they are the same act — get the free
 * opening, be on the list for launch. NO release date anywhere: "coming soon",
 * never a month or a year (agents.md §4). This page is the paid-ad destination,
 * so it must get STRONGER for the change, never quieter: a single ask, no
 * competing button, and the launch list stated plainly as what you also get.
 *
 * TITLE COLLISION: the bare word "Exploit" is owned by hacking content — always
 * compound ("Exploit — the novel", "Book One of The Universe", the byline). No
 * invented plot facts: every beat below is lifted from app/elements/egt.exploit.js.
 *
 * Renders inside the EGT shell (S.page); gated dark with /exploit via
 * egt.exploit.config.isEnabled() in app/routes/egt.pages.js (default ON).
 */

var _ = require("../../uam/module.js")(module.exports, {
  namespace: "egt.exploit.free",
  title: "EXPLOIT free-chapters landing",
  description: "Book One of The Universe — the free-opening email-capture landing for paid arrivals.",
  supportServer: true
});
var S = require("../helpers/egt.sections.js");

/* ---- small copy helpers (semantic + reveal hooks) ----
 * Every string runs through S.priceToken so any "$75 + shipping" gets the
 * unbreakable span structurally (S.priceToken passes non-matching strings and
 * node arrays straight through). */
function micro(text, mod) { return { n: "p", c: "egt-free__micro" + (mod ? " " + mod : ""), i: S.priceToken(text) }; }
function eyebrow(text) { return { n: "p", c: "egt-free__eyebrow egt-free-reveal", i: text }; }
function h(text, mod) { return { n: "h2", c: "egt-free__h" + (mod ? " " + mod : "") + " egt-free-reveal", i: S.priceToken(text) }; }
function body(nodes) { return { n: "p", c: "egt-free__body egt-free-reveal", i: S.priceToken(nodes) }; }

/* the free-opening capture — the one true door (source:"free-chapter"). Rendered
 * twice on the page (hero + close) with distinct ids; both self-segment and both
 * open the shared confirm dialog. */
function freeCapture(id) {
  return S.capture({
    source: "free-chapter",
    id: id,
    variant: "panel",
    heading: "h2",
    title: "Where should we send your free chapters?",
    /* Label kept ONLY for screen readers (S.capture forces a label — omitting it
     * defaults to visible "Be first to know."). It is visually hidden via
     * .egt-free .egt-cap__label in egt.exploit.free.scss so nothing but the input
     * shows under the title (founder 2026-08-05). */
    label: "Your email address",
    cta: "Send me the free chapters",
    consent: true,
    dialog: true,
    consentLabel: [ "Yes — email me the free opening of ", { n: "em", i: "Exploit" }, ", then new chapters, album drops and news from the studio. I can unsubscribe from any email." ],
    dialogLead: "You're confirmed, and the free opening of Exploit is yours. Tap Read your free chapters to start.",
    note: "You agree to the "
  });
}

/* ===================================================================
 * HERO (above the fold) — message match + capture + scroll cue.
 * The book art breathes undimmed behind the copy (it is near-black wherever the
 * type sits — the same asset /exploit reads); contrast for the headline comes
 * from the art being dark, and for the form from its own frosted card. NO scrim.
 * ================================================================= */
function hero() {
  return { n: "section", c: "egt-free__hero", id: "free-hero", "data-free-top": "true",
    i: [
      { n: "div", c: "egt-free__hero-media", "aria-hidden": "true", "data-free-parallax": "0.18",
        i: { n: "img", c: "egt-free__hero-img", src: "/imgs/exploit/exploit-hero-bg.jpg",
          alt: "", loading: "eager", decoding: "async", fetchpriority: "high" } },
      { n: "div", c: "egt__shell egt-free__hero-inner",
        i: [
          /* Two-column on wide screens: the headline block LEFT, the email capture
           * RIGHT, both vertically centred — so the FIELD + primary CTA sit inside
           * the first viewport on desktop (defect fix: the tall single stack pushed
           * the capture ~1.5 screens down). On narrow it collapses to one column,
           * capture directly under the subhead. */
          { n: "div", c: "egt-free__hero-grid",
            i: [
              /* HERO copy — the message-matched variant is now the ONLY hero
               * (founder 2026-08-05: the A/B test is retired, A deleted, B kept).
               * It mirrors the ad promise ("you're the one everyone leans on") so
               * paid + organic social arrivals get a matched message, then moves the
               * darkness to the second sentence. From HERO-PAID-VARIANT.md. */
              { n: "div", c: "egt-free__hero-copy",
                i: [
                  micro("A cinematic spiritual thriller · for anyone who's ever had to be the strong one", "egt-free__micro--genre"),
                  /* Book title. DESKTOP shows the wordmark IMAGE; MOBILE hides the
                   * image and shows the compact text "Exploit" instead (founder
                   * 2026-08-05) so the hero stays short + the email is above the
                   * fold. Toggle lives in egt.exploit.free.scss. */
                  { n: "img", c: "egt-free__hero-title-card egt-free-reveal egt-exploit-title-card-mobile-remove-class", src: "/imgs/exploit/exploit-title-card.avif",
                    alt: "Exploit", loading: "eager", decoding: "async" },
                  { n: "p", c: "egt-free__hero-title-text egt-free-reveal", i: "Exploit" },
                  { n: "h1", c: "egt-free__h1 egt-free-reveal",
                    i: [ "You're the one everyone leans on.",
                      { n: "span", c: "egt-free__h1-turn", i: "This is the story of who catches you." } ] },
                ] },
              { n: "div", c: "egt-free__hero-cap egt-free-reveal",
                i: [
                  freeCapture("egt-cap-free")//,
                  // micro("For readers of Frank Peretti and Ted Dekker — a 750-page novel you can start free.", "egt-free__micro--proof")
                ] },
              /* Subhead is its OWN hero-grid child so mobile can drop it BELOW the
               * capture — order becomes headline → email field → story, so the email
               * lands above the fold on phones. On desktop it sits under the headline
               * in the left column (grid placement in egt.exploit.free.scss). */
              { n: "p", c: "egt-free__sub egt-free__hero-sub egt-free-reveal",
                i: [ { n: "em", i: "Exploit" }, " is for the strong one who's quietly breaking. An innocent man loses everything — then finds seven people who won't let him fall, and a light he can't explain. It goes to dark places on purpose, and it never leaves you there." ] }
            ] },
          /* the scroll cue is the LAST child of the hero column, in NORMAL FLOW and
           * BELOW the two-column grid, so it can never sit on top of the CTA/capture
           * (founder instruction; the /exploit hero learned this the hard way). */
          { n: "a", c: "egt-free__scroll egt-free-reveal", href: "#free-story",
            i: [ { n: "span", c: "egt-free__scroll-lbl", i: "The story" },
              { n: "span", c: "egt-free__scroll-arrow", "aria-hidden": "true", i: "↓" } ] }
        ] }
    ] };
}

/* ===================================================================
 * BELOW THE FOLD — the emotional, story-based unfolding. Frosted glass over
 * undimmed art where there is art; the true-black base elsewhere.
 * ================================================================= */

/* full-bleed media section (real art background + frosted card). No scrim —
 * contrast is the frosted card only (agents.md §4). Mirrors /exploit's mediaSection. */
/* Phone focus for the widescreen 4K backgrounds (desktop shows ~the whole
 * frame; a tall phone section shows a slice) — picked by eye from each frame. */
var BG_FOCUS = { "war-green-sphere": "50% 55%", "magic-the-light": "56% 45%", "loc-rooftop": "35% 50%" };
function bgFocus(src) {
  var k = String(src).replace(/^.*\//, "").replace(/\.[a-z]+$/, "");
  return "object-position:" + (BG_FOCUS[k] || "50% 50%");
}

function mediaSection(mod, img, id, inner) {
  return { n: "section", c: "egt-free__sec egt-free__media-sec " + mod, id: id || undefined,
    i: [
      { n: "div", c: "egt-free__sec-media", "aria-hidden": "true", "data-free-parallax": "0.08",
        i: { n: "img", c: "egt-free__sec-img", src: img, style: bgFocus(img), alt: "", loading: "lazy", decoding: "async" } },
      { n: "div", c: "egt__shell egt-free__sec-inner",
        i: { n: "div", c: "egt-glass egt-glass--dark egt-free__media-card", i: inner } }
    ] };
}

/* THE HOOK / logline — over the war-green-sphere art. */
function secHook() {
  var lines = [
    { n: "p", c: "egt-free__hook-lead egt-free-reveal", i: [ "He was never the only one ", { n: "span", c: "egt-free__nowrap", i: "standing." } ] },
    { n: "p", c: "egt-free__hook-line egt-free-reveal", i: "An innocent man, marked by the oldest dark power in the world. Everyone he loves — taken. He's left alive to feel it." },
    { n: "p", c: "egt-free__hook-line egt-free-reveal", i: "But every system has a flaw. A touch of magic, long forgotten. A cheat code buried under everything." },
    { n: "p", c: "egt-free__hook-beat egt-free-reveal", i: "Eight souls. One secret war. One reveal." },
    { n: "p", c: "egt-free__hook-line egt-free-reveal", i: [ "Let's call it an ", { n: "span", c: "egt-free__green", i: "Exploit." } ] }
  ];
  return mediaSection("egt-free__sec--hook", "/imgs/exploit/war-green-sphere.jpg", "free-story",
    [ { n: "div", c: "egt-free__hook", i: lines } ]);
}

/* WHAT LANDS IN YOUR INBOX — the honest, instant-gratification detail (§5.6). */
function secGet() {
  var items = [
    { k: "The Foreword", v: "Why this story exists, in the author's own voice." },
    { k: "The Prologue", v: "A man in a dark coat walks off the tallest building at sunset, smiling." },
    { k: "Chapters 1–7", v: "The murder, the scattered family, and the first turn of the war." }
  ].map(function (e) {
    return { n: "div", c: "egt-free__get egt-free-reveal",
      i: [ { n: "span", c: "egt-free__get-k", i: e.k }, { n: "span", c: "egt-free__get-v", i: e.v } ] };
  });
  return { n: "section", c: "egt-free__sec egt-free__center",
    i: { n: "div", c: "egt__shell",
      i: [ eyebrow("What opens for you"),
        h("The opening of the book — yours to keep."),
        { n: "div", c: "egt-free__gets", i: items },
        micro("Leave your address, type the code we email you, and the opening is yours right away — available now, no waiting.", "egt-free__micro--caption"),
        { n: "div", c: "egt-free__cta-row egt-free-reveal",
          i: { n: "a", c: "egt-free__cta egt-free__cta--ghost", href: "#free-hero",
            i: { n: "span", i: "Get the free chapters ↑" } } } ] } };
}

/* THE STAKES — the severance (dark type beat; honest, no fabricated art). */
function secStakes() {
  return { n: "section", c: "egt-free__sec egt-free__dark egt-free__center",
    i: { n: "div", c: "egt__shell egt-free__sec-inner",
      i: [ eyebrow("The order"),
        h("Kill everyone he loves. Leave him alive underneath it."),
        body("An order older than the century decides one man is dangerous — and that a martyr is useless. So they don't come for him. They come for everyone he belongs to. A country away, one photograph on their board has no mark across it. That's where the war they started turns around."),
        micro("A scattered family is harder to erase than one man.", "egt-free__micro--caption") ] } };
}

/* THE EIGHT SOULS — compact cinematic teaser (real portraits, lazy). */
var SOULS = [
  { name: "Jonathan Avery Karros", essence: "The showman who carries a warmth he never earned.", img: "soul-karros" },
  { name: "Reyna", essence: "The sister who turned the assassin sent for her children — and put him to work.", img: "soul-reyna" },
  { name: "Ronan", essence: "His best friend, and more than he seems. He won't let a broken man fall.", img: "soul-ronan" },
  { name: "Niko", essence: "The enemy's blunt instrument, with a conscience that won't stay dead.", img: "soul-niko" }
];
function soulCard(s) {
  return { n: "article", c: "egt-free__soul egt-free-reveal",
    i: [
      { n: "div", c: "egt-free__soul-portrait",
        i: { n: "img", c: "egt-free__soul-img", src: "/imgs/exploit/" + s.img + ".jpg",
          alt: s.name, loading: "lazy", decoding: "async" } },
      { n: "h3", c: "egt-free__soul-name", i: s.name },
      { n: "p", c: "egt-free__soul-essence", i: s.essence }
    ] };
}
function secSouls() {
  return { n: "section", c: "egt-free__sec egt-free__dark egt-free__center",
    i: { n: "div", c: "egt__shell",
      i: [ eyebrow("The eight souls"),
        h("A family scattered across a war — and refusing to fall alone."),
        { n: "div", c: "egt-free__souls", i: SOULS.map(soulCard) },
        micro("They came to break one man. They never counted the hands reaching back.", "egt-free__micro--caption") ] } };
}

/* THE AUTHOR — a short manifesto, the E-E-A-T / experience anchor (research §3.3). */
function secAuthor() {
  return { n: "section", c: "egt-free__sec egt-free__warm",
    i: { n: "div", c: "egt__shell egt-free__author",
      i: [ eyebrow("Why this story"),
        h("I wrote the book I needed to believe was true."),
        { n: "blockquote", c: "egt-free__manifesto egt-free-reveal",
          i: "People get an enormous amount thrown at them. Things get destroyed. What I wanted to put on the page is the thing I keep finding underneath the wreckage — that it is not all lost, that no one is falling as alone as they feel, and that there's a plan running under all of it we don't get to fully see. I open dark on purpose. The hope has to have something to shine against." },
        { n: "p", c: "egt-free__signature egt-free-reveal", i: "— Justin K Kazmierczak" } ] } };
}

/* SECONDARY CTA — push to the full microsite (founder instruction). Secondary to
 * the capture; the capture is the primary conversion. */
function secWhole() {
  return { n: "section", c: "egt-free__sec egt-free__center",
    i: { n: "div", c: "egt__shell",
      i: [ eyebrow("The whole story"),
        h("The whole world is waiting."),
        body([ { n: "em", i: "Exploit" }, " is Book One of ", { n: "strong", i: "The Universe" }, " — a complete story you can start and finish, scored act by act, with all three acts of the score already out and free to stream. The book itself is coming soon." ]),
        { n: "div", c: "egt-free__cta-row egt-free-reveal",
          i: { n: "a", c: "egt-free__cta egt-free__cta--ghost", href: "/exploit",
            i: { n: "span", i: "See the whole story →" } } } ] } };
}

/* FAQ — money-safe, honest answers, single-sourced into FAQPage JSON-LD. FAQ
 * rich results are retired (2026); kept for GEO / AI-answer extraction. */
var FREE_FAQ = [
  ["What do I get for free?", "The opening of the novel — the Foreword, the Prologue and Chapters 1–7. It's free, and it commits you to nothing."],
  ["How much of the book is it?", "The first seven chapters plus the Foreword and Prologue — a real, substantial start, not a teaser."],
  ["Is this a Christian or faith-based book?", "It's a spiritual thriller — supernatural, with a real war between light and an ancient darkness, written for readers who love Frank Peretti and Ted Dekker. It goes to dark places on purpose, and it never leaves you there."],
  ["When does it arrive?", "Right away. The sample is live now — enter your email, type the code we send you, and the free opening opens on the page. No waiting."],
  /* 2026-08-21 founder directive: the book is not sold anywhere on this site,
   * so the old "what does it cost to preorder" answer (price, certificate, The
   * Named) is gone. These two take its place and answer what a reader on a paid
   * ad actually asks next: when can I get the whole thing, and what am I
   * signing up for. COMING SOON — never a month, a year or a window. */
  ["When does the whole book come out?", "Coming soon. We are not putting a date on it until we can stand behind one. Ask for the free opening above and you are on the list — when the book lands, the people on that list are the ones we tell."],
  ["What am I signing up for?", "The free opening first, then new chapters as they are released, the album drops, and the word the day the book is out. One list, and you can unsubscribe from any email."]
];
function faqItem(q, a) {
  return { n: "details", c: "egt-free__faq egt-free-reveal",
    i: [ { n: "summary", c: "egt-free__faq-q", i: q }, { n: "div", c: "egt-free__faq-a", i: S.priceToken(a) } ] };
}
function secFaq() {
  return { n: "section", c: "egt-free__sec egt-free__center",
    i: { n: "div", c: "egt__shell",
      i: [ eyebrow("Questions"),
        h("The honest answers."),
        { n: "div", c: "egt-free__faqs", i: FREE_FAQ.map(function (qa) { return faqItem(qa[0], qa[1]); }) } ] } };
}

/* CLOSE — a second capture for readers who scrolled past the hero (research: the
 * hero does ~80%, this catches the rest), then the emotional close. */
function secClose() {
  return { n: "section", c: "egt-free__sec egt-free__dark egt-free__center egt-free__close",
    i: { n: "div", c: "egt__shell",
      i: [ eyebrow("Read it first"),
        { n: "h2", c: "egt-free__h egt-free-reveal", i: [ "Start the story ", { n: "span", c: "egt-free__gold", i: "for free." } ] },
        { n: "p", c: "egt-free__sub egt-free-reveal", i: "The Foreword, the Prologue, and Chapters 1–7. Be one of the hands reaching back." },
        { n: "div", c: "egt-free__close-cap egt-free-reveal", i: freeCapture("egt-cap-free-2") },
        /* This slot used to hold a ghost "Or preorder the first edition →" link,
         * put here at the founder's instruction for the reader who already knew
         * they wanted the book. The 2026-08-21 directive removes the preorder
         * everywhere, and a paid landing page is the LAST place to leave a
         * second, competing ask — so the slot now carries the other half of the
         * offer as a plain line, not a button: the list. Nothing on this page
         * asks for anything except the email address. */
        micro("The book is coming soon — and this list is how you hear it first.",
          "egt-free__micro--trust"),
        { n: "p", c: "egt-free__close-line egt-free-reveal", i: "No one falls alone." },
        micro("Book One of The Universe · a novel by Justin K Kazmierczak", "egt-free__micro--trust") ] } };
}

/* THE MUSIC OF THE BOOK — an understated closing link to the Exploit soundtrack.
 * Secondary to the free-sample capture (still the primary CTA). Renders a lockup
 * for EVERY streamable Exploit act, in ALBUMS order, from the single source
 * S.ALBUMS — so Act 1 + Act 2 show now and Act 3 auto-joins the moment it's
 * streamable (no future edit). Each lockup links to its real detail page
 * (/entertainment/<id>, where the previews live). No invented route. */
function secMusic() {
  var acts = S.ALBUMS.filter(function (a) {
    return a && typeof a.id === "string" && a.id.indexOf("exploit-act-") === 0 && S.albumStreamable(a);
  });
  /* Nothing streamable yet → don't render an empty section. */
  if (!acts.length) { return null; }
  return { n: "section", c: "egt-free__sec egt-free__center egt-free__music",
    i: { n: "div", c: "egt__shell",
      i: [
        { n: "p", c: "egt-free__music-eyebrow egt-free-reveal", i: "The music of the book" },
        { n: "p", c: "egt-free__music-title egt-free-reveal", i: "Hear the story's soundtrack." },
        { n: "p", c: "egt-free__music-sub egt-free-reveal",
          i: "Every song is sung from inside the story. Tap an act to listen." },
        { n: "div", c: "egt-free__music-grid egt-free-reveal",
          i: acts.map(function (a) {
            return { n: "a", c: "egt-free__music-card", href: "/entertainment/" + a.id,
              "aria-label": "Listen to " + a.nm + " — songs from the novel",
              i: [
                { n: "div", c: "egt-free__music-art",
                  i: { n: "img", c: "egt-free__music-cover", src: a.art,
                    alt: a.nm + " — album cover", width: "700", height: "700",
                    loading: "lazy", decoding: "async" } },
                { n: "div", c: "egt-free__music-meta",
                  i: [
                    { n: "p", c: "egt-free__music-card-title", i: a.nm },
                    { n: "span", c: "egt-free__music-link", i: "Listen →" }
                  ] }
              ] };
          }) },
        micro([ "Or explore the whole catalogue at ",
          { n: "a", c: "egt-free__inline-link", href: "/entertainment", i: "EGT Entertainment" }, "." ],
          "egt-free__micro--caption")
      ] } };
}

/* Per-surface JSON-LD: WebPage + BreadcrumbList + Book (the /exploit#book entity)
 * + FAQPage. Facts mirror /exploit (all visibly on-page or already public).
 *
 * NO `offers` BLOCK — removed 2026-08-21 with the preorder, exactly as on
 * /exploit. It declared availability PreOrder, price 75 USD and a $7.99 US
 * shipping rate against an offer URL of /exploit/preorder, which now 404s: a
 * machine-readable offer for something this site does not sell. A Book with no
 * live offer carries no Offer. NO datePublished / releaseDate either — the book
 * is "coming soon" and there is no date to publish. Organization + WebSite come
 * from S.page (siteSchema); the author @id binds this Book to the founder
 * Person, who worksFor that Organization. */
function freeSchema() {
  var O = S.siteOrigin();
  return S.jsonLd([
    { "@type": "WebPage", "@id": O + "/exploit/free#webpage", url: O + "/exploit/free",
      name: "Read the opening of Exploit — free · Book One of The Universe",
      description: "Read the first chapters of Exploit — free: the Foreword, Prologue and Chapters 1–7 of the cinematic spiritual thriller by Justin K Kazmierczak.",
      isPartOf: { "@id": O + "/#website" },
      about: { "@id": O + "/exploit#book" },
      breadcrumb: { "@id": O + "/exploit/free#breadcrumb" } },
    { "@type": "BreadcrumbList", "@id": O + "/exploit/free#breadcrumb",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: O + "/" },
        { "@type": "ListItem", position: 2, name: "Exploit: Book One of The Universe", item: O + "/exploit" },
        { "@type": "ListItem", position: 3, name: "Read the opening free" }
      ] },
    /* The SAME Book entity as /exploit (@id /exploit#book), not a second
     * /exploit/free#book (SEO wave 2B-1, 2026-10-09). Keep these facts
     * identical to exploitSchema() in egt.exploit.js — two pages describing
     * one @id differently is a self-contradicting graph. Book only (no
     * Product: there is no offer). The free-opening specifics live on the
     * WebPage description above. */
    { "@type": "Book", "@id": O + "/exploit#book", name: "Exploit: Book One of The Universe",
      alternateName: ["Exploit", "Exploit (novel)", "Exploit: Book One of The Universe"],
      author: { "@id": O + "/founder#person" },
      isPartOf: { "@type": "BookSeries", "@id": O + "/exploit#series", name: "The Universe" },
      genre: "Spiritual thriller",
      inLanguage: "en", bookFormat: "https://schema.org/Hardcover",
      url: O + "/exploit",
      description: "Book One of The Universe — a cinematic spiritual thriller by Justin K Kazmierczak. The opening chapters are free to read now; the book is coming soon.",
      image: O + "/imgs/exploit/exploit-title-card.avif" },
    /* The author, by the founder page's Person @id (one entity site-wide);
     * name + url repeated so this page's graph resolves on its own. */
    { "@type": "Person", "@id": O + "/founder#person",
      name: "Justin K Kazmierczak", url: O + "/founder" },
    { "@type": "FAQPage", "@id": O + "/exploit/free#faq",
      mainEntity: FREE_FAQ.map(function (qa) {
        return { "@type": "Question", name: qa[0],
          acceptedAnswer: { "@type": "Answer", text: qa[1] } };
      }) }
  ]);
}

exports.render = async function render() {
  var content = { n: "div", c: "egt-free", "data-free-root": "true",
    i: [ freeSchema(), hero(),
      secHook(), secGet(), secStakes(), secSouls(), secAuthor(),
      secWhole(), secFaq(), secClose(), secMusic() ].filter(Boolean) };
  /* Structural price-token integrity check (H18): every visible "$75 + shipping"
   * must render inside an egt-price-token span or this THROWS. */
  S.assertPriceTokensGuarded(content);
  /* Minimal, LOCKED nav for this paid-arrival route (conversion fix): brand + a
   * single CTA that smooth-scrolls to the capture, with the room links AND the
   * rotating announce banner suppressed so nothing competes with the free offer.
   * Both flags are OPT-IN and additive (S.page/navV2 default to the full chrome),
   * so every other surface keeps its full nav + banner. */
  return S.page("/exploit", [ content ], {
    navCta: { label: "Read the opening free", href: "#free-hero" },
    minimalNav: true,
    hideAnnounce: true
  });
};
